import { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';

// pdf.js is loaded lazily and runs WITHOUT a separate worker file: importing
// pdf.worker.entry embeds the engine in the app bundle (window.pdfjsWorker),
// so there is nothing external to fetch/fail on any device or browser.
let _libPromise = null;
function loadPdfLib() {
  if (!_libPromise) {
    _libPromise = Promise.all([
      import('pdfjs-dist/legacy/build/pdf'),
      import('pdfjs-dist/legacy/build/pdf.worker.entry'),
    ]).then(([lib]) => lib);
  }
  return _libPromise;
}

// Renders every page of a PDF blob URL onto canvases — works on mobile.
export default function PdfViewer({ url, className = '' }) {
  const containerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function render() {
      if (!url || !containerRef.current) return;
      setLoading(true);
      setError(null);
      try {
        const pdfjsLib = await loadPdfLib();
        const buffer = await (await fetch(url)).arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: buffer }).promise;
        if (cancelled) return;
        const container = containerRef.current;
        container.innerHTML = '';
        const cssWidth = container.clientWidth || 600;
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        for (let i = 1; i <= pdf.numPages; i += 1) {
          const page = await pdf.getPage(i);
          if (cancelled) return;
          const base = page.getViewport({ scale: 1 });
          const scale = (cssWidth / base.width) * dpr;
          const vp = page.getViewport({ scale });
          const canvas = document.createElement('canvas');
          canvas.width = vp.width;
          canvas.height = vp.height;
          canvas.style.width = '100%';
          canvas.style.display = 'block';
          canvas.style.background = '#fff';
          canvas.style.marginBottom = '10px';
          canvas.style.borderRadius = '2px';
          container.appendChild(canvas);
          await page.render({ canvasContext: canvas.getContext('2d'), viewport: vp }).promise;
        }
      } catch (e) {
        console.error('PdfViewer render failed:', e);
        if (!cancelled) setError(`Could not display this PDF (${e?.message || 'unknown error'})`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    render();
    return () => { cancelled = true; };
  }, [url]);

  return (
    <div className={className} data-testid="pdf-viewer">
      {loading && (
        <div className="flex items-center justify-center py-10 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading document…
        </div>
      )}
      {error && <p className="text-sm text-red-400 py-6 text-center px-4">{error}</p>}
      <div ref={containerRef} />
    </div>
  );
}
